import React, { useEffect } from 'react'
import { useLazyQuery } from '@apollo/client/react'
import { Maybe } from 'graphql/jsutils/Maybe'
import { When } from 'react-if'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { slugs } from '@/constants/slugs'
import { useLanding } from '@/contexts/LandingContext'
import { FeaturedProjectSection } from '@/organisms/FeaturedProjectSection'
import { InvestmentBanner } from '@/organisms/InvestmentBanner'
import { AngelInvestmentBanner } from '@/organisms/InvestmentBanner/AngelInvestmentBanner'
import { Page } from '@/page'
import { getContentfulClient } from '@/services/ApolloClient'
import { getFeaturedProjectsQuery } from '@/services/CmsService/FeaturedProject'
import { InvestmentBannerResult } from '@/services/CmsService/InvestmentBanner'
import { contentfulFaqLocations, useProjectFaqs } from '@/services/FaqService'
import { useGuildMember } from '@/services/GuildUserService'
import { PageDataContext, PageModel } from '@/services/RenderService'
import { useUserRegion } from '@/services/UserRegionService'
import { useUser } from '@/services/UserService'
import { GetFeaturedProjectsQuery } from '@/types/codegen-contentful'
import { isGuildMostlySupported } from '@/utils/GuildUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { AppPromoSection } from '@/views/LandingView/AppPromoSection'
import { CreatePromoSection } from '@/views/LandingView/CreatePromoSection'
import { FeaturedShowSection } from '@/views/LandingView/FeaturedShowSection'
import { AngelCommunity } from './AngelCommunitySection'
import { FaqSection } from './FaqSection'
import { JoinTheMovement } from './JoinTheMovementSection'
import { MainHeroSection } from './MainHeroSection'
import { WatchForFree } from './WatchForFreeSection'

interface LandingView {
  investmentBanner: Maybe<InvestmentBannerResult>
  preview: boolean
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
}

export const LandingView: React.FC<LandingView> = ({ investmentBanner, preview, page, pageDataContext }) => {
  const { locale } = useLocale()
  const { watchForFree } = useLanding()
  const { isGuildMember, guildPermissions } = useGuildMember()
  const { region } = useUserRegion()
  const { isLoggedIn } = useUser()

  const { faqs, loadingProjectFaqs } = useProjectFaqs({
    locale,
    preview,
    projectSlug: slugs.angelStudios,
    location: contentfulFaqLocations.home,
  })

  const client = getContentfulClient({ locale, preview })
  const [getFeaturedProjects, { data: featuredProjectsData, loading: loadingFeaturedProjects }] =
    useLazyQuery<GetFeaturedProjectsQuery>(getFeaturedProjectsQuery, {
      client,
      errorPolicy: 'all',
    })

  useEffect(() => {
    getFeaturedProjects({ variables: { locale, preview } })
  }, [getFeaturedProjects, locale, preview])

  const shouldPromoteGuild = isGuildMostlySupported(region)
  const firstFeaturedProjectId = featuredProjectsData?.featuredProjectCollection?.items?.[0]?.sys?.id

  return (
    <div className="mx-auto flex flex-col bg-core-gray-950 text-white">
      <MainHeroSection
        client={client}
        isLoggedIn={isLoggedIn}
        loading={false}
        preview={preview}
        isGuildMember={isGuildMember}
        guildPermissions={guildPermissions}
      />
      <AngelInvestmentBanner />
      {investmentBanner && <InvestmentBanner {...investmentBanner} className="my-6 md:my-8 lg:my-12" />}
      {!!page && !!pageDataContext && (
        <section className="flex flex-col gap-y-12 md:pt-4 lg:pt-0">
          <Page page={page} pageDataContext={{ ...pageDataContext, contentItemConfig: { padding: true } }} />
        </section>
      )}
      <When condition={Boolean(watchForFree)}>
        <WatchForFree watchForFree={watchForFree} isLoggedIn={isLoggedIn} />
      </When>
      <LazyHydrate whenVisible>
        <When condition={!isGuildMember}>
          <JoinTheMovement />
        </When>
      </LazyHydrate>
      <LazyHydrate whenVisible>
        <AngelCommunity />
      </LazyHydrate>
      <div className="bg-core-gray-950 pt-4">
        <AppPromoSection />
      </div>

      <When condition={shouldPromoteGuild && !loadingFeaturedProjects && firstFeaturedProjectId}>
        <FeaturedProjectSection featuredProjectId={firstFeaturedProjectId as string} />
      </When>

      <div className="bg-core-gray-950 pb-12">
        <LazyHydrate whenVisible>
          <FeaturedShowSection />
        </LazyHydrate>
      </div>

      <CreatePromoSection />

      {!loadingProjectFaqs && <FaqSection faqs={faqs} />}
    </div>
  )
}
